import React from 'react';
import { useDashboard } from '../DashboardContext';

const PERIOD_LABELS: Record<string, string> = {
    monthly:    'This Month',
    quarterly:  'This Quarter',
    halfyearly: 'This Half-Year',
    annual:     'This Year',
};

const EventActivity: React.FC = () => {
    const { eventActivity, loading, timeframe } = useDashboard();

    // Card entirely absent when loading or no events (no empty div, no placeholder)
    if (loading || !eventActivity || eventActivity.registered === 0) return null;

    const periodLabel = PERIOD_LABELS[timeframe] ?? 'This Period';
    const registered  = eventActivity.registered ?? 0;
    const attended    = eventActivity.attended;   // null = not captured yet

    // Attendance % — only if attendance data is available
    const attendancePct = attended !== null && registered > 0
        ? Math.round((attended / registered) * 100)
        : null;

    const attendanceBadgeStyle = attendancePct !== null
        ? {
            background: attendancePct >= 75 ? '#dcfce7' : '#fef3c7',
            color:      attendancePct >= 75 ? '#15803d' : '#92400e',
            borderRadius: 20,
            padding: '2px 10px',
            fontSize: 11,
            fontWeight: 600,
          }
        : null;

    return (
        <div className="mb-4">
            {/* Section label — absent when card absent */}
            <div className="card">
                {/* Card Header: title · period label + attendance badge */}
                <div className="card-header bg-white d-flex justify-content-between align-items-center py-2">
                    <div>
                        {/* Dynamic title: "📅 Event Activity" or "📅 Event Activity (N events)" */}
                        <div className="fw-semibold font-14">
                            📅 Event Activity
                        </div>
                        {/* Sub-label: {N} event(s) · {period label} */}
                        <div className="text-muted small">
                            {registered} registration{registered !== 1 ? 's' : ''} · {periodLabel}
                        </div>
                    </div>
                    {/* Average attendance % badge — right side */}
                    {attendanceBadgeStyle ? (
                        <span style={attendanceBadgeStyle}>
                            {attendancePct}% attend
                        </span>
                    ) : (
                        <span className="text-muted small" style={{ fontSize: 11 }}>
                            Attendance not captured
                        </span>
                    )}
                </div>

                {/* Event Row — full-width horizontal per spec */}
                <div className="card-body p-0">
                    <div className="d-flex align-items-center gap-3 px-4 py-3">

                        {/* Date badge: Day (large, bold, orange) + Month (small caps) */}
                        <div className="text-center flex-shrink-0 rounded"
                            style={{
                                background: '#fff3e0',
                                border: '1px solid #f97316',
                                minWidth: 56,
                                padding: '8px 12px',
                            }}>
                            <div className="fw-bold" style={{ fontSize: 24, color: '#ea580c', lineHeight: 1 }}>
                                {eventActivity.day || '—'}
                            </div>
                            <div className="text-uppercase text-muted" style={{ fontSize: 10 }}>
                                {eventActivity.month || '—'}
                            </div>
                        </div>

                        {/* Event name + type (centre) */}
                        <div className="flex-1">
                            <div className="fw-semibold font-13">Event Registrations This Period</div>
                            <div className="d-flex align-items-center gap-2 mt-1">
                                <span className="badge rounded-pill"
                                    style={{ background: '#f0fdf4', color: '#15803d', fontSize: 11 }}>
                                    🏫 Physical
                                </span>
                                <span className="text-muted" style={{ fontSize: 11 }}>
                                    {eventActivity.day} {eventActivity.month}
                                </span>
                            </div>
                        </div>

                        {/* Stats group — right-aligned */}
                        <div className="d-flex align-items-center gap-2 flex-shrink-0">
                            <div className="text-center">
                                <div className="fw-bold font-14">{registered.toLocaleString()}</div>
                                <div className="text-muted" style={{ fontSize: 10 }}>Registered</div>
                            </div>
                            <div className="vr mx-1 opacity-25" style={{ height: 28 }}></div>
                            <div className="text-center">
                                <div className="fw-bold font-14">{attended !== null ? attended : '—'}</div>
                                <div className="text-muted" style={{ fontSize: 10 }}>Attended</div>
                            </div>
                            {/* Attendance % badge */}
                            {attendancePct !== null && (
                                <>
                                    <div className="vr mx-1 opacity-25" style={{ height: 28 }}></div>
                                    <span className="badge rounded-pill"
                                        style={{
                                            background: attendancePct >= 75 ? '#dcfce7' : '#fef3c7',
                                            color:      attendancePct >= 75 ? '#15803d' : '#92400e',
                                            fontSize: 12, fontWeight: 600,
                                        }}>
                                        {attendancePct}%
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventActivity;
